import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertiesApi, expensesApi, formatCurrency } from '@/lib/api';
import { 
  Building2, Plus, Search, Filter, MoreVertical, 
  Edit, Trash2, Eye, Archive, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import PropertyModal from '@/components/PropertyModal';
import AppLayout from '@/components/AppLayout';

export default function PropertiesPage() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [propertyExpenses, setPropertyExpenses] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propsRes, expensesRes] = await Promise.all([
        propertiesApi.getAll(),
        expensesApi.getAll(),
      ]);
      setProperties(propsRes.data);
      
      // Group expenses by property
      const expensesByProperty = {};
      expensesRes.data.forEach(expense => {
        if (!expensesByProperty[expense.property_id]) {
          expensesByProperty[expense.property_id] = 0;
        }
        expensesByProperty[expense.property_id] += expense.monthly_amount;
      });
      setPropertyExpenses(expensesByProperty);
    } catch (error) {
      toast.error('Failed to load properties');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProperty(null);
    setModalOpen(true);
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setModalOpen(true);
  };

  const handleDelete = (property) => {
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!propertyToDelete) return;
    
    try {
      await propertiesApi.delete(propertyToDelete.id);
      toast.success('Property deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete property');
    } finally {
      setDeleteDialogOpen(false);
      setPropertyToDelete(null);
    }
  };

  const toggleStatus = async (property) => {
    const newStatus = property.status === 'active' ? 'archived' : 'active';
    try {
      await propertiesApi.update(property.id, { status: newStatus });
      toast.success(`Property ${newStatus === 'active' ? 'activated' : 'archived'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredProperties = properties.filter(p => {
    const matchesSearch = p.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="spinner w-8 h-8"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-8" data-testid="properties-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-ink font-display tracking-tight">Properties</h1>
            <p className="mt-1 text-muted-brand">
              {properties.length} {properties.length === 1 ? 'property' : 'properties'} in your portfolio
            </p>
          </div>
          <Button 
            onClick={handleCreate}
            data-testid="create-property-btn" 
            className="bg-anchor-blue hover:bg-anchor-blue/90 rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add property
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search properties..."
              data-testid="property-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Properties Grid */}
        {filteredProperties.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="properties-list">
            {filteredProperties.map((property) => {
              const totalExpenses = propertyExpenses[property.id] || 0;
              
              return (
                <Card 
                  key={property.id} 
                  className="border-slate-200 hover:shadow-md transition-shadow duration-200"
                  data-testid={`property-card-${property.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge 
                            variant="outline"
                            className={property.status === 'active' 
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                              : 'bg-slate-100 text-slate-500 border-slate-200'
                            }
                          >
                            {property.status === 'active' ? 'Active' : 'Archived'}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-900 truncate font-display">
                          {property.address}
                        </h3>
                        {property.notes && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                            {property.notes}
                          </p>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`property-menu-${property.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/properties/${property.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(property)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => toggleStatus(property)}>
                            {property.status === 'active' ? (
                              <>
                                <Archive className="w-4 h-4 mr-2" />
                                Archive
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Activate
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleDelete(property)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Financial Info */}
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Monthly Rent</p>
                          <p className="text-sm font-semibold text-emerald-600">
                            {formatCurrency(property.monthly_rent)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Monthly Expenses</p>
                          <p className="text-sm font-semibold text-red-600">
                            {formatCurrency(totalExpenses)}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <div className="flex justify-between items-center">
                          <p className="text-xs text-slate-500">Net Income</p>
                          <p className={`text-sm font-semibold ${
                            property.monthly_rent - totalExpenses >= 0 
                              ? 'text-emerald-600' 
                              : 'text-red-600'
                          }`}>
                            {formatCurrency(property.monthly_rent - totalExpenses)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <Button
                      variant="ghost"
                      className="w-full mt-4 text-slate-600 hover:text-slate-900"
                      onClick={() => navigate(`/properties/${property.id}`)}
                      data-testid={`view-property-${property.id}`}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 font-display">
                {searchTerm || statusFilter !== 'all' ? 'No properties found' : 'No properties yet'}
              </h3>
              <p className="text-sm text-slate-500 mt-1 text-center max-w-md">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Add your first property to get started with Portfolio Pal'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <Button 
                  onClick={handleCreate}
                  className="mt-6 bg-slate-900 hover:bg-slate-800"
                  data-testid="empty-state-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Property
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Property Modal */}
      <PropertyModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        property={editingProperty}
        onSuccess={() => {
          setModalOpen(false);
          fetchData();
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{propertyToDelete?.address}"? This will also delete all associated documents, expenses, and key dates. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              data-testid="delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
